<script setup>
import { Head } from '@inertiajs/vue3';
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { fontsHref, rootClass, rootStyle, templateFor, usesTrailheadBlocks } from './templates';
import { siteComponentsFor } from './siteComponents';
import { sitePrefix } from './siteLocation.js';
import ThEventsIndex from './Trailhead/ThEventsIndex.vue';
import TemplatePreviewBar from './TemplatePreviewBar.vue';

const props = defineProps({
    // Title, description, share image and favicon, resolved
    // server-side by SiteMeta so every page type agrees.
    meta: { type: Object, default: () => ({}) },
    org: { type: Object, required: true },
    layout: { type: Object, required: true },
    upcoming: { type: Array, default: () => [] },
    past: { type: Array, default: () => [] },
    site_data: { type: Object, default: () => ({}) },
    is_preview: { type: Boolean, default: false },
    preview_reason: { type: String, default: null },
});

// The chosen template's renderers. A template is a design only, so
// every block below reads the same config whichever one is picked.
const COMPONENTS = siteComponentsFor(props.layout.theme);
provide('tiqBlockComponents', COMPONENTS);
// Shared chrome may not inject these, but provide nulls so any nested
// block that does (e.g. a hand-built columns block in a footer) gets
// the same defaults Show.vue gives it.
provide('tiqCurrentPost', null);
provide('tiqCurrentEvent', null);

// Trailhead draws this page from its own blocks; Classic keeps the
// markup below. A template is a design, so both read the same props.
const isTrailhead = usesTrailheadBlocks(templateFor(props.layout.theme).key);

const accent = computed(() => props.layout.theme?.accent || '#dd9b36');

// Filter unknown block types out so a stale block doesn't take down
// the iteration (matches Show.vue's defensiveness).
const headerBlocks = computed(() => (props.layout.shared_header || []).filter((b) => COMPONENTS[b.type]));
const footerBlocks = computed(() => (props.layout.shared_footer || []).filter((b) => COMPONENTS[b.type]));

// Group events by month label ("June 2026") for stacked rendering.
function groupByMonth(events) {
    const groups = [];
    let cur = null;
    for (const e of events) {
        const d = e.date ? new Date(e.date + 'T00:00:00') : null;
        if (!d) continue;
        const key = d.toLocaleDateString([], { month: 'long', year: 'numeric' });
        if (!cur || cur.label !== key) {
            cur = { label: key, items: [] };
            groups.push(cur);
        }
        cur.items.push(e);
    }
    return groups;
}

// Tag chip filter — derived from the upcoming list so the chips
// reflect what visitors actually have to choose from. Past events
// are ignored for chip-discovery (a tag that only exists in the
// archive shouldn't appear as a filter for upcoming).
const tagOptions = computed(() => {
    const seen = new Map();
    for (const e of props.upcoming) {
        if (!e.tag) continue;
        const key = String(e.tag).trim();
        if (!key) continue;
        seen.set(key.toLowerCase(), key);
    }
    return Array.from(seen.values()).sort();
});
const activeTag = ref(null);  // null = "All"
function matchesActiveTag(e) {
    if (!activeTag.value) return true;
    return (e.tag || '').toLowerCase() === activeTag.value.toLowerCase();
}
const filteredUpcoming = computed(() => props.upcoming.filter(matchesActiveTag));
const filteredPast = computed(() => props.past.filter(matchesActiveTag));
const upcomingGroups = computed(() => groupByMonth(filteredUpcoming.value));
const pastGroups = computed(() => groupByMonth(filteredPast.value));

const showPast = ref(false);

function eventTime(e) {
    if (e.all_day) return 'All day';
    const fmt = (t) => (t ? t.slice(0, 5) : '');
    if (e.start_time && e.end_time) return `${fmt(e.start_time)}–${fmt(e.end_time)}`;
    return fmt(e.start_time) || '';
}
function eventDay(e) {
    if (!e.date) return '';
    return new Date(e.date + 'T00:00:00').toLocaleDateString([], { weekday: 'short', day: 'numeric' });
}

// Canonical /site/{slug}/ link-rewriting (same logic as Show.vue).
const basePath = ref('');
function detectBase() {
    basePath.value = sitePrefix();
}
function onClick(e) {
    if (!basePath.value) return;
    const a = e.target.closest && e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('/') || href.startsWith('//')) return;
    if (href.startsWith(basePath.value + '/') || href === basePath.value || href === basePath.value + '/') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    window.location.href = basePath.value + href;
}
onMounted(() => { detectBase(); document.addEventListener('click', onClick); });
onBeforeUnmount(() => document.removeEventListener('click', onClick));

// Where to point the "Subscribe (iCal)" link. Same logic as the
// basePath rewriter: vanity domains live at /events.ics, canonical
// preview lives at /site/{slug}/events.ics.
const icalHref = computed(() => basePath.value ? `${basePath.value}/events.ics` : '/events.ics');
</script>

<template>
    <Head>
        <title>{{ meta?.title || org.name }}</title>
        <!-- Description, canonical, favicon and the Open Graph /
             Twitter tags are rendered by the shell (app.blade.php)
             from `meta`, where a link preview can read them. -->
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link rel="stylesheet" :href="fontsHref(layout.theme)">
    </Head>
    <div class="tiq-site" :class="[rootClass(layout.theme), { 'has-preview-banner': is_preview }]" :style="rootStyle(layout.theme)">
        <TemplatePreviewBar :theme="layout.theme" />
        <div v-if="is_preview" class="tiq-preview-banner">
            <span>{{ preview_reason || 'Preview — this site is currently hidden from visitors.' }}</span>
        </div>

        <template v-for="block in headerBlocks" :key="`sh-${block.id}`">
            <component :is="COMPONENTS[block.type]"
                       :config="block.config"
                       :accent="accent"
                       :org="org"
                       :site_data="site_data" />
        </template>

        <main v-if="isTrailhead" class="th-index-page">
            <ThEventsIndex :upcoming="upcoming" :ical-href="icalHref" :accent="accent" :org="org" />
        </main>
        <main v-else class="tiq-events-index">
            <header class="tiq-eix-header" :style="{ borderLeftColor: accent }">
                <span class="tiq-eix-eyebrow" :style="{ color: accent }">What's on</span>
                <h1>Events</h1>
                <p>Upcoming workdays, rides, races, and gatherings.</p>
                <a :href="icalHref" class="tiq-eix-ical" :style="{ borderColor: accent, color: accent }">
                    📅 Subscribe (iCal)
                </a>
            </header>

            <nav v-if="tagOptions.length > 0" class="tiq-eix-tags" aria-label="Filter events by tag">
                <button type="button"
                        @click="activeTag = null"
                        class="tiq-eix-chip"
                        :class="{ 'is-active': activeTag === null }"
                        :style="activeTag === null ? { backgroundColor: accent, borderColor: accent } : null">
                    All
                </button>
                <button v-for="tag in tagOptions" :key="tag"
                        type="button"
                        @click="activeTag = (activeTag && activeTag.toLowerCase() === tag.toLowerCase()) ? null : tag"
                        class="tiq-eix-chip"
                        :class="{ 'is-active': activeTag && activeTag.toLowerCase() === tag.toLowerCase() }"
                        :style="activeTag && activeTag.toLowerCase() === tag.toLowerCase() ? { backgroundColor: accent, borderColor: accent } : null">
                    {{ tag }}
                </button>
            </nav>

            <section v-if="upcoming.length === 0" class="tiq-eix-empty">
                <p>No upcoming events. Check back soon.</p>
            </section>
            <section v-else-if="filteredUpcoming.length === 0" class="tiq-eix-empty">
                <p>No upcoming events tagged "{{ activeTag }}". <button @click="activeTag = null" class="tiq-eix-clear" :style="{ color: accent }">Clear filter</button></p>
            </section>

            <section v-for="group in upcomingGroups" :key="`u-${group.label}`" class="tiq-eix-group">
                <h2>{{ group.label }}</h2>
                <a v-for="e in group.items" :key="e.id" :href="e.href" class="tiq-eix-row">
                    <div class="tiq-eix-when" :style="{ backgroundColor: accent }">
                        <span class="tiq-eix-day">{{ eventDay(e) }}</span>
                        <span v-if="eventTime(e)" class="tiq-eix-time">{{ eventTime(e) }}</span>
                    </div>
                    <div class="tiq-eix-body">
                        <h3>{{ e.title }}</h3>
                        <div class="tiq-eix-meta">
                            <span v-if="e.location">📍 {{ e.location }}</span>
                            <span v-if="e.tag" class="tiq-eix-tag">{{ e.tag }}</span>
                            <span v-if="e.audience">{{ e.audience }}</span>
                            <span v-if="e.is_recurring" class="tiq-eix-recurring">↻ Recurring</span>
                            <span v-if="e.signups_enabled" class="tiq-eix-going" :style="{ color: accent }">
                                {{ e.signups_taken }}<span v-if="e.signups_capacity">/{{ e.signups_capacity }}</span> going
                            </span>
                        </div>
                        <p v-if="e.excerpt" class="tiq-eix-excerpt">{{ e.excerpt }}</p>
                    </div>
                </a>
            </section>

            <!-- Past events are no longer shown publicly. Managers
                 still see the full history in the dashboard. -->
        </main>

        <template v-for="block in footerBlocks" :key="`sf-${block.id}`">
            <component :is="COMPONENTS[block.type]"
                       :config="block.config"
                       :accent="accent"
                       :org="org"
                       :site_data="site_data" />
        </template>
    </div>
</template>

<style>
/* Borrow the global wrapper styles + preview banner from Show.vue. */
.tiq-site { font-family: 'Barlow', sans-serif; color: #1a1a1a; background: #f8f8f8; min-height: 100vh; }
.tiq-site * { box-sizing: border-box; }
.tiq-preview-banner {
    position: fixed; top: 0; left: 0; right: 0; z-index: 9999;
    background: #fde68a; color: #78350f; text-align: center;
    padding: 8px 16px;
    font-family: 'Barlow Condensed', sans-serif;
    text-transform: uppercase; letter-spacing: 0.1em;
    font-size: 12px; font-weight: 600; height: 32px;
}
.tiq-site.has-preview-banner { padding-top: 32px; }
.tiq-site.has-preview-banner .tiq-nav { top: 32px !important; }
</style>

<style scoped>
/* Top padding clears the 68px fixed MenuBlock (+ preview banner
   when shown). Other pages rely on a hero block to push past the
   nav; the events index has no hero, so we add the offset here. */
.tiq-events-index { max-width: 880px; margin: 0 auto; padding: 124px 24px 96px; }
.has-preview-banner .tiq-events-index { padding-top: 156px; }

.tiq-eix-header {
    border-left: 4px solid currentColor;
    padding-left: 16px;
    margin-bottom: 40px;
}
.tiq-eix-eyebrow {
    display: block;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 12px; font-weight: 700;
    letter-spacing: 0.32em; text-transform: uppercase;
    margin-bottom: 10px;
}
.tiq-eix-header h1 {
    font-family: 'Bebas Neue', sans-serif; font-weight: bold;
    font-size: clamp(48px, 7vw, 80px);
    letter-spacing: 0.04em; line-height: 1; margin: 0;
}
.tiq-eix-header p { margin: 12px 0 0; color: #6b7280; font-size: 17px; }
.tiq-eix-ical {
    display: inline-flex; align-items: center; gap: 6px;
    margin-top: 16px; padding: 6px 14px;
    border: 1.5px solid currentColor; border-radius: 999px;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 13px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.1em;
    text-decoration: none;
}
.tiq-eix-ical:hover { background: rgba(0,0,0,0.04); }

.tiq-eix-tags {
    display: flex; flex-wrap: wrap; gap: 8px;
    margin-bottom: 28px;
}
.tiq-eix-chip {
    background: #fff;
    border: 1.5px solid #d1d5db;
    color: #4b5563;
    padding: 5px 14px;
    border-radius: 999px;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 12px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.12em;
    cursor: pointer;
    transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.tiq-eix-chip:hover { border-color: #6b7280; color: #1a1a1a; }
.tiq-eix-chip.is-active { color: #fff; }

.tiq-eix-empty {
    background: #fff; border: 1px dashed #d1d5db; border-radius: 6px;
    padding: 40px; text-align: center; color: #9ca3af;
}
.tiq-eix-clear {
    background: none; border: 0; padding: 0; cursor: pointer;
    text-decoration: underline; font: inherit;
}

.tiq-eix-group { margin-bottom: 32px; }
.tiq-eix-group h2 {
    font-family: 'Barlow Condensed', sans-serif;
    text-transform: uppercase; letter-spacing: 0.12em;
    font-size: 13px; color: #6b7280; font-weight: 700;
    margin: 0 0 12px; padding: 0 4px;
}

.tiq-eix-row {
    display: grid; grid-template-columns: 96px 1fr; gap: 18px;
    padding: 16px; background: #fff; border: 1px solid #e5e7eb;
    border-radius: 6px; margin-bottom: 10px;
    text-decoration: none; color: inherit;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.tiq-eix-row:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.08); }

.tiq-eix-when {
    color: #fff; text-align: center; padding: 12px 8px;
    border-radius: 4px; align-self: start;
    font-family: 'Barlow Condensed', sans-serif;
}
.tiq-eix-when.is-past { background: #9ca3af !important; }
.tiq-eix-day {
    display: block;
    font-family: 'Bebas Neue', sans-serif; font-weight: bold;
    font-size: 22px; letter-spacing: 0.04em;
    text-transform: uppercase;
}
.tiq-eix-time {
    display: block; font-size: 12px;
    letter-spacing: 0.08em; margin-top: 4px;
    opacity: 0.9;
}

.tiq-eix-body h3 {
    font-family: 'Bebas Neue', sans-serif; font-weight: bold;
    font-size: 24px; letter-spacing: 0.02em;
    margin: 0 0 6px;
}
.tiq-eix-meta {
    display: flex; flex-wrap: wrap; gap: 14px;
    font-size: 13px; color: #6b7280;
    font-family: 'Barlow', sans-serif;
}
.tiq-eix-tag {
    background: #f3f4f6; padding: 2px 10px; border-radius: 2px;
    text-transform: uppercase; letter-spacing: 0.08em;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 11px; font-weight: 700;
}
.tiq-eix-recurring,
.tiq-eix-going {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.08em;
    color: #6b7280;
}
.tiq-eix-excerpt {
    margin: 8px 0 0; color: #4b5563; font-size: 14px; line-height: 1.5;
}

.tiq-eix-past { margin-top: 56px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
.tiq-eix-past-toggle {
    background: transparent; border: 0; cursor: pointer;
    font-family: 'Barlow Condensed', sans-serif;
    text-transform: uppercase; letter-spacing: 0.12em;
    font-size: 13px; font-weight: 700; color: #6b7280;
    padding: 8px 0;
}
.tiq-eix-past-toggle:hover { color: #1a1a1a; }
.tiq-eix-past-list { margin-top: 16px; }
.tiq-eix-group.is-past .tiq-eix-row { opacity: 0.7; }
.tiq-eix-group.is-past .tiq-eix-row:hover { opacity: 1; }

@media (max-width: 600px) {
    .tiq-eix-row { grid-template-columns: 72px 1fr; gap: 12px; padding: 12px; }
    .tiq-eix-day { font-size: 18px; }
}
</style>
